// Loads the MediaInfo.js library (WASM when available) exactly once and returns
// a promise that resolves to the initialised library instance. Every uploader
// widget shares this loader so the script is fetched once and callers can never
// touch the `MediaInfoLib` global before it exists.

import { markRaw } from 'vue';

let libPromise = null;

const SCRIPT_ID = 'mediainfo-js-shared';
// Older widgets injected the script under this id; treat it as the same library.
const LEGACY_SCRIPT_ID = 'mediainfo-js';
const LOAD_TIMEOUT_MS = 30000;

function getMediaInfoGlobal() {
  if (typeof window === 'undefined') return undefined;
  return window.MediaInfoLib;
}

function createLibInstance() {
  const MediaInfoLib = getMediaInfoGlobal();
  if (typeof MediaInfoLib !== 'function') {
    return Promise.reject(new ReferenceError('MediaInfoLib is not defined'));
  }

  return new Promise((resolve, reject) => {
    try {
      // markRaw keeps the emscripten module out of Vue's reactivity system when
      // widgets store it in component state.
      let lib = MediaInfoLib({
        postRun() {
          if (typeof Promise !== 'undefined' && lib instanceof Promise) {
            lib.then((module) => resolve(markRaw(module))).catch(reject);
          } else {
            resolve(markRaw(lib));
          }
        },
      });
    } catch (e) {
      reject(e);
    }
  });
}

function whenScriptSettles(el) {
  return new Promise((resolve, reject) => {
    el.addEventListener('load', () => resolve(), { once: true });
    el.addEventListener('error', () => reject(new Error('Failed to load MediaInfo script')), { once: true });
  });
}

// Covers scripts whose load event already fired before we started listening.
function pollForGlobal(timeoutMs, state) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const tick = () => {
      if (state.done) {
        resolve();
      } else if (getMediaInfoGlobal()) {
        resolve();
      } else if (Date.now() - startedAt > timeoutMs) {
        reject(new Error('Timed out waiting for MediaInfo library'));
      } else {
        setTimeout(tick, 100);
      }
    };
    tick();
  });
}

function injectScript() {
  const scriptEl = document.createElement('script');
  scriptEl.src = 'WebAssembly' in window
    ? 'https://castr.com/libs/mediainfojs2/MediaInfoWasm.js'
    : 'https://castr.com/libs/mediainfojs2/MediaInfo.js';
  scriptEl.setAttribute('id', SCRIPT_ID);

  const settled = whenScriptSettles(scriptEl);
  document.head.appendChild(scriptEl);
  return settled;
}

export function loadMediaInfoLib() {
  if (libPromise) return libPromise;

  libPromise = (async () => {
    if (getMediaInfoGlobal()) return createLibInstance();

    const existing =
      document.getElementById(SCRIPT_ID) || document.getElementById(LEGACY_SCRIPT_ID);

    if (existing) {
      const state = { done: false };
      try {
        await Promise.race([whenScriptSettles(existing), pollForGlobal(LOAD_TIMEOUT_MS, state)]);
      } catch (e) {
        // Fall through and load our own copy.
      } finally {
        state.done = true;
      }
      if (getMediaInfoGlobal()) return createLibInstance();
    }

    await injectScript();
    return createLibInstance();
  })().catch((err) => {
    // Allow a later caller to retry after a failed load.
    libPromise = null;
    throw err;
  });

  return libPromise;
}
